import { TextField } from '@cambridgeassessment/cambridge-ui';
import { useEffect, useState } from 'react';
import { StyledModeratorList, StyledModeratorListItem, StyledSmartSearch } from './PostResultsPage.styles';
import { useDebounce, SHORT_DEBOUNCE_TIMEOUT } from 'lib-browser';
import { ModeratorInfo } from '@cambridgeassessment/lib-ee-commons';

type Props = {
  moderators: ModeratorInfo[];
  allocatedModerator?: ModeratorInfo;
  onModeratorSelect: (moderator: ModeratorInfo | null) => void;
};

export const SmartModeratorSearch = (props: Props): JSX.Element => {
  const { moderators, allocatedModerator } = props;
  const [filteredModerators, setFilteredModerators] = useState<ModeratorInfo[] | undefined>(undefined);
  const [searchModerator, setSearchModerator] = useState<string>('');
  const [selectedModerator, setSelectedModerator] = useState<string>('');
  const debouncedFilter = useDebounce<string>(searchModerator, SHORT_DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (allocatedModerator) {
      handleModeratorSelection(allocatedModerator);
      setSelectedModerator(
        `${allocatedModerator.examinerNumber} ${allocatedModerator.name} (${allocatedModerator.creditorNumber})`,
      );
    }
  }, [allocatedModerator]);

  useEffect(() => {
    if (debouncedFilter.length) {
      const moderatorFilter = moderators?.filter((moderator) => {
        const moderatorName = moderator.name.toLowerCase();
        const moderatorCreditorNumber = moderator.creditorNumber.toString();
        return [moderatorName, moderatorCreditorNumber].join(' ').includes(debouncedFilter.toLowerCase());
      });
      setFilteredModerators(moderatorFilter);
    } else {
      setFilteredModerators([]);
    }
  }, [debouncedFilter]);

  const handleModeratorChanges = (value: string): void => {
    setSelectedModerator('');
    setSearchModerator(value);
    setFilteredModerators(undefined);
    props.onModeratorSelect(null);
  };

  const handleModeratorSelection = (moderator: ModeratorInfo): void => {
    setSearchModerator('');
    setSelectedModerator(`${moderator.examinerNumber} ${moderator.name} (${moderator.creditorNumber})`);
    setFilteredModerators([]);
    props.onModeratorSelect(moderator);
  };

  return (
    <StyledSmartSearch>
      <TextField
        disabled={!!allocatedModerator}
        dataQaId='search-moderator'
        value={selectedModerator ? selectedModerator : searchModerator}
        onChange={handleModeratorChanges}
        placeholder='Search for moderator by name or creditor number'
      />
      {filteredModerators && searchModerator !== '' && (
        <StyledModeratorList>
          {filteredModerators.length ? (
            filteredModerators.map((moderator) => {
              return (
                <StyledModeratorListItem
                  key={moderator.creditorNumber}
                  onClick={() => handleModeratorSelection(moderator)}
                >
                  {moderator.examinerNumber} {moderator.name} ({moderator.creditorNumber})
                </StyledModeratorListItem>
              );
            })
          ) : (
            <StyledModeratorListItem>No result</StyledModeratorListItem>
          )}
        </StyledModeratorList>
      )}
    </StyledSmartSearch>
  );
};
